package com.kanati.admin.features.readiness

import kotlinx.serialization.json.*
import kotlin.js.Date

// UI projection only. The operational wire payload remains owned by Platform Backend.
data class ReadinessAssessment(val ready: Boolean, val generatedAt: String, val checks: List<ReadinessItem>)
data class ReadinessItem(val label: String, val state: String, val message: String, val diagnostics: List<String>)

fun parseReadiness(payload: JsonObject): ReadinessAssessment {
    fun JsonObject.flag(key: String): Boolean = getValue(key).jsonPrimitive.let {
        require(!it.isString)
        requireNotNull(it.booleanOrNull)
    }
    fun JsonObject.text(key: String): String = getValue(key).jsonPrimitive.let {
        require(it.isString && it.content.isNotBlank())
        it.content
    }
    val ready = payload.flag("gameNightMvpReady")
    val status = payload.text("status")
    require(status == if (ready) "ready" else "not_ready")
    val checks = payload.getValue("checks").jsonArray
    require(checks.isNotEmpty())
    val items = checks.map { element ->
        val check = element.jsonObject
        val name = check.text("name")
        val passed = check.flag("ready")
        val required = check.flag("required")
        val diagnostics = (check["details"] as? JsonObject).orEmpty().mapNotNull { (key, value) ->
            val raw = (value as? JsonPrimitive)?.contentOrNull
            when {
                key in presenceLabels && raw in listOf("true", "false") ->
                    "${presenceLabels.getValue(key)}: ${if (raw == "true") "present" else "missing"}"
                key == "enabledFlag" && raw in listOf("true", "false") ->
                    "Android Tap to Pay: ${if (raw == "true") "enabled" else "disabled"}"
                key == "missingEnv" -> raw?.split(',')?.filter { it in settingNames }
                    ?.takeIf { it.isNotEmpty() }?.joinToString(prefix = "Settings to check: ")
                else -> null // Never render arbitrary diagnostic values, URLs or credentials.
            }
        }
        ReadinessItem(
            label = checkLabels[name] ?: "Additional backend check",
            state = if (passed) "PASS" else if (required) "FAIL" else "WARN",
            message = if (!passed && name in setOf("database", "adminSessionSigning", "cors"))
                "Required configuration is missing. Ask the deployment operator to review this check."
            else check.text("message"),
            diagnostics = diagnostics,
        )
    }
    // Reject an internally inconsistent payload; do not substitute a locally computed assessment.
    require(ready == checks.filter { it.jsonObject.flag("required") }.all { it.jsonObject.flag("ready") })
    val generatedAt = payload.text("generatedAt")
    require(!Date(generatedAt).getTime().isNaN())
    return ReadinessAssessment(ready, generatedAt, items)
}

private val checkLabels = mapOf(
    "database" to "Database configuration", "adminSessionSigning" to "Admin session signing",
    "eventScopedTicketValidation" to "Ticketing · online event validation",
    "ticketReports" to "Reporting · ticket sales and check-in activity", "cors" to "Browser access · CORS",
    "consumerCardPayments" to "Consumer card payments", "stripeTerminal" to "POS card · Stripe Terminal",
    "tapToPayAndroid" to "POS card · Android Tap to Pay",
)
private val presenceLabels = mapOf(
    "urlConfigured" to "Database URL", "userConfigured" to "Database user", "secretConfigured" to "Session signing",
    "secretKeyConfigured" to "Stripe secret key", "publishableKeyConfigured" to "Stripe publishable key",
    "terminalLocationConfigured" to "Terminal location",
)
private val settingNames = setOf(
    "KANATI_STRIPE_SECRET_KEY", "KANATI_STRIPE_PUBLISHABLE_KEY",
    "KANATI_STRIPE_TAP_TO_PAY_ANDROID_ENABLED", "KANATI_STRIPE_TERMINAL_LOCATION_ID",
)
